import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { apiFetch } from '../api';
import ChatBadges from '../components/ChatBadges';
import LobbyUserAvatar from '../components/LobbyUserAvatar';
import TableGrid from '../components/TableGrid';
import TableView from '../components/TableView';
import UserModMenu from '../components/UserModMenu';
import { visualSeatClass } from '../utils/tableSeats';
import {
  applyCommandSuggestion,
  autocompleteCommands,
  mergeCommandCatalog,
} from '../utils/chatCommands';
import './LobbyRoom.css';

const SEAT_ORDER_MERGE = ['north', 'east', 'south', 'west'];
const TABLE_LOAD_TIMEOUT_MS = 8000;
const LOBBY_READY_FALLBACK_MS = 2500;

/** Hard gone — leave table view. Transient seat/view errors are retried via resync. */
const TABLE_GONE_RE = /not found|no longer|removed|unavailable|cannot join/i;
/** Soft errors — keep table UI open and pull fresh state (do not stand/abandon). */
const TABLE_RESYNC_RE = /not at this table|not at a table|Seat is reserved|reconnecting|Could not load|out of sync|stale/i;
/** Play/pass/bid rule hints — toast above south hand (Spades + Hearts). */
const GAME_RULE_TOAST_MS = 3000;
const GAME_RULE_MSG_RE = /must (play|follow|lead|be seated)|cannot lead|not your turn|not in your hand|not playing|not bidding|not allowed|first trick|2 of clubs|select (exactly )?3|already passed|not in pass|spades until|hearts until|bid must|pass phase|queen of spades|no hearts|not choosing|double nil|card not in|cannot raise|hand not visible|not part of this game|no active game/i;

function tableStorageKey(lobbyId) {
  return `cards:lobby:${lobbyId}:table`;
}

function seatForUserId(seats, userId) {
  if (!userId || !seats) return null;
  for (const seat of SEAT_ORDER_MERGE) {
    const occ = seats[seat];
    if (occ?.type === 'player' && Number(occ.userId) === Number(userId)) {
      return seat;
    }
  }
  return null;
}

/** Public table:state + per-socket table:private (myHand never broadcast). */
function mergeTableState(publicTable, privateViewer, loggedInUserId) {
  if (!publicTable) return null;
  const priv = privateViewer || {};
  const viewerSeat = priv.viewerSeat ?? seatForUserId(publicTable.seats, loggedInUserId);
  const isHost = loggedInUserId != null
    && publicTable.hostKind === 'human'
    && publicTable.hostUserId != null
    && Number(publicTable.hostUserId) === Number(loggedInUserId);
  const phase = publicTable.game?.phase;
  const biddingChoice = publicTable.game?.biddingChoice;
  const doubleNil = !!publicTable.spadesSettings?.doubleNilEnabled;
  let myHand = priv.myHand ?? null;
  let myHandMeta = priv.myHandMeta ?? null;
  let peekHand = priv.peekHand ?? null;
  if (phase === 'dealing' || phase === 'idle' || !phase) {
    myHand = null;
    myHandMeta = null;
    peekHand = null;
  } else if (doubleNil && phase === 'bidding_choice' && viewerSeat && biddingChoice?.[viewerSeat] == null) {
    myHand = null;
    myHandMeta = null;
  }
  return {
    ...publicTable,
    viewerSeat,
    isWatching: priv.isWatching != null ? !!priv.isWatching : !viewerSeat,
    isHost,
    myHand,
    myHandMeta,
    peekHand,
    watcherPeekSeat: priv.watcherPeekSeat ?? null,
  };
}

function privateFieldsFromTableState(state) {
  if (!state) return null;
  return {
    tableId: state.id,
    viewerSeat: state.viewerSeat,
    isWatching: state.isWatching,
    myHand: state.myHand,
    myHandMeta: state.myHandMeta,
    peekHand: state.peekHand,
    watcherPeekSeat: state.watcherPeekSeat,
  };
}

function ChatUsername({
  msg,
  canModerate,
  onClick,
}) {
  const clickable = canModerate && msg.userId;
  const className = ['lobby-username', clickable ? 'lobby-username--mod' : ''].filter(Boolean).join(' ');

  const content = (
    <>
      <ChatBadges roleBadge={msg.roleBadge} clubBadge={msg.clubBadge} />
      <span>{msg.username || '?'}</span>
    </>
  );

  if (!clickable) {
    return <span className={className}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => onClick(msg, e.currentTarget.getBoundingClientRect())}
    >
      {content}
    </button>
  );
}

function LobbyRoom({ loggedInUser, currentUserId, sessionReady, onRequireLogin }) {
  const { lobbyId } = useParams();
  const [title, setTitle] = useState('Lobby');
  const [gameType, setGameType] = useState('spades');
  /** Active games enabled for this lobby — shown next to the title */
  const [availableGames, setAvailableGames] = useState([]);
  const [presence, setPresence] = useState({ count: 0, users: [] });
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatCommands, setChatCommands] = useState([]);
  const [chatCmdSuggestIdx, setChatCmdSuggestIdx] = useState(0);
  const [disconnectMsg, setDisconnectMsg] = useState(null);
  const [connected, setConnected] = useState(false);
  const [moderation, setModeration] = useState(null);
  const [canManageLobbies, setCanManageLobbies] = useState(false);
  const [chatMuted, setChatMuted] = useState(false);
  const [modTarget, setModTarget] = useState(null);
  const [modAnchor, setModAnchor] = useState(null);
  const [modFlash, setModFlash] = useState(null);

  const [tables, setTables] = useState([]);
  const [activeTableId, setActiveTableId] = useState(null);
  const [tableState, setTableState] = useState(null);
  const [tablePresence, setTablePresence] = useState({ count: 0, users: [] });
  const [tableMessages, setTableMessages] = useState([]);
  const [chatTab, setChatTab] = useState('lobby');
  const [chatUnread, setChatUnread] = useState({
    lobby: false,
    table: false,
    lobbyImportant: false,
    tableImportant: false,
  });
  const [tableError, setTableError] = useState(null);
  /** Single play-rule toast above south hand; new msgs reset the dismiss timer. */
  const [ruleToast, setRuleToast] = useState(null);
  const [dealFlash, setDealFlash] = useState(null);
  /** Latest gin action event for table animations (draw/discard/knock) */
  const [ginGameEvent, setGinGameEvent] = useState(null);
  const [chatBubbles, setChatBubbles] = useState([]);
  const bubbleSeqRef = useRef(0);
  const lastBubbleKeyRef = useRef(null);
  const tableMsgLenRef = useRef(0);
  const ruleToastTimerRef = useRef(null);

  const socketRef = useRef(null);
  const dealFlashTimerRef = useRef(null);
  const viewerSeatRef = useRef(null);
  const perspectiveSeatRef = useRef(null);
  const heartbeatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const tableMessagesEndRef = useRef(null);
  const activeTableIdRef = useRef(null);
  const tableStateRef = useRef(null);
  const lobbyReadyTimerRef = useRef(null);
  const privateViewerRef = useRef(null);
  const loggedInUserIdRef = useRef(currentUserId);
  const chatTabRef = useRef('lobby');
  const inTableViewRef = useRef(false);
  /** After Back/leave, ignore late table:state that would re-open the table UI. */
  const suppressTableViewRef = useRef(false);

  const canModerate = moderation?.can_moderate_lobby || moderation?.can_moderate_global;
  const inTableView = !!activeTableId && !!tableState;
  const chatCommandSuggestions = autocompleteCommands(chatInput, chatCommands);

  loggedInUserIdRef.current = currentUserId;
  chatTabRef.current = chatTab;
  inTableViewRef.current = inTableView;
  const sidebarUsers = presence.users || [];
  const sidebarTitle = 'Online';
  const sidebarCount = presence.count;
  const activeMessages = chatTab === 'table' && inTableView ? tableMessages : messages;
  const activeChatMuted = chatMuted;

  /** Push overlay chat bubbles while seated at a table (esp. mobile compact chat). */
  const pushChatBubble = useCallback((msg) => {
    if (!msg) return;
    const key = `${msg.ts || ''}|${msg.userId || ''}|${msg.username || ''}|${msg.text || ''}|${msg.type || ''}`;
    if (lastBubbleKeyRef.current === key) return;
    lastBubbleKeyRef.current = key;
    bubbleSeqRef.current += 1;
    const id = `b-${bubbleSeqRef.current}`;
    setChatBubbles((prev) => {
      const next = [...prev, {
        id,
        type: msg.type || 'user',
        username: msg.username,
        text: msg.text || '',
        highlight: !!msg.highlight,
      }];
      return next.slice(-4);
    });
    setTimeout(() => {
      setChatBubbles((prev) => prev.filter((b) => b.id !== id));
    }, 5200);
  }, []);

  /** One rule toast at a time; re-trigger only updates text + resets 3s timer. */
  const showRuleToast = useCallback((message) => {
    const text = String(message || '').trim();
    if (!text) return;
    setRuleToast(text);
    if (ruleToastTimerRef.current) {
      clearTimeout(ruleToastTimerRef.current);
    }
    ruleToastTimerRef.current = setTimeout(() => {
      ruleToastTimerRef.current = null;
      setRuleToast(null);
    }, GAME_RULE_TOAST_MS);
  }, []);

  useEffect(() => {
    activeTableIdRef.current = activeTableId;
  }, [activeTableId]);

  useEffect(() => {
    if (!inTableView) {
      tableMsgLenRef.current = 0;
      setChatBubbles([]);
      setRuleToast(null);
      if (ruleToastTimerRef.current) {
        clearTimeout(ruleToastTimerRef.current);
        ruleToastTimerRef.current = null;
      }
      return;
    }
    const prevLen = tableMsgLenRef.current;
    if (tableMessages.length > prevLen) {
      for (let i = prevLen; i < tableMessages.length; i += 1) {
        pushChatBubble(tableMessages[i]);
      }
    }
    tableMsgLenRef.current = tableMessages.length;
  }, [tableMessages, inTableView, pushChatBubble]);

  useEffect(() => {
    tableStateRef.current = tableState;
  }, [tableState]);

  useEffect(() => {
    viewerSeatRef.current = tableState?.viewerSeat ?? null;
    const peek = tableState?.isWatching && tableState?.watcherPeekSeat
      ? tableState.watcherPeekSeat
      : null;
    perspectiveSeatRef.current = tableState?.viewerSeat ?? peek ?? null;
  }, [tableState?.viewerSeat, tableState?.isWatching, tableState?.watcherPeekSeat]);

  const HEARTBEAT_MS = 10000;

  const persistTableId = useCallback((tableId) => {
    const key = tableStorageKey(lobbyId);
    if (tableId) sessionStorage.setItem(key, String(tableId));
    else sessionStorage.removeItem(key);
  }, [lobbyId]);

  const markChatUnread = useCallback((channel, msg) => {
    if (!inTableViewRef.current) return;
    if (chatTabRef.current === channel) return;
    if (msg?.type === 'user' && msg.userId != null
      && Number(msg.userId) === Number(loggedInUserIdRef.current)) {
      return;
    }
    const important = !!msg?.highlight;
    setChatUnread((prev) => ({
      ...prev,
      [channel]: true,
      [`${channel}Important`]: prev[`${channel}Important`] || important,
    }));
  }, []);

  const clearChatUnread = useCallback((channel) => {
    setChatUnread((prev) => ({
      ...prev,
      [channel]: false,
      [`${channel}Important`]: false,
    }));
  }, []);

  const selectChatTab = useCallback((tab) => {
    setChatTab(tab);
    clearChatUnread(tab);
  }, [clearChatUnread]);

  const exitTableView = useCallback((message) => {
    // Block late table:state / private / resync from re-opening the table shell.
    suppressTableViewRef.current = true;
    activeTableIdRef.current = null;
    privateViewerRef.current = null;
    persistTableId(null);
    setActiveTableId(null);
    setTableState(null);
    setTablePresence({ count: 0, users: [] });
    setTableMessages([]);
    setChatTab('lobby');
    setChatUnread({
      lobby: false,
      table: false,
      lobbyImportant: false,
      tableImportant: false,
    });
    if (message) {
      setTableError(message);
      setTimeout(() => setTableError(null), 5000);
    }
  }, [persistTableId]);

  /** Soft desync: keep table shell + seat; request server snapshot. Never stand/leave. */
  const requestTableResync = useCallback((message) => {
    if (suppressTableViewRef.current) return;
    const tableId = activeTableIdRef.current;
    const socket = socketRef.current;
    if (tableId && socket?.connected) {
      socket.emit('table:resync', { tableId });
      socket.emit('lobby:heartbeat');
    }
    if (message) {
      setTableError(message);
      setTimeout(() => setTableError(null), 5000);
    }
  }, []);

  const sendGoodbye = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    try {
      socket.emit('lobby:goodbye');
    } catch {
      /* tab may be unloading */
    }
  }, []);

  const teardownSocket = useCallback((opts = {}) => {
    if (lobbyReadyTimerRef.current) {
      clearTimeout(lobbyReadyTimerRef.current);
      lobbyReadyTimerRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (socketRef.current) {
      if (opts.goodbye) sendGoodbye();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnected(false);
  }, [sendGoodbye]);

  const showDisconnect = useCallback((message, promptLogin = false) => {
    exitTableView();
    setDisconnectMsg(message || 'You were disconnected from the lobby.');
    teardownSocket();
    if (promptLogin) {
      onRequireLogin?.(message);
    }
  }, [exitTableView, onRequireLogin, teardownSocket]);

  const connectSocket = useCallback((joinPayload) => {
    teardownSocket();

    const socket = io({
      path: joinPayload.socketPath,
      query: joinPayload.socketQuery,
      auth: { token: joinPayload.joinToken },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const clearLobbyReadyFallback = () => {
      if (lobbyReadyTimerRef.current) {
        clearTimeout(lobbyReadyTimerRef.current);
        lobbyReadyTimerRef.current = null;
      }
    };

    socket.on('connect', () => {
      setConnected(true);
      setDisconnectMsg(null);
      socket.emit('lobby:heartbeat');
      /* After reconnect / wake, pull authoritative table state so bid/play UI matches server. */
      if (activeTableIdRef.current && !suppressTableViewRef.current) {
        socket.emit('table:resync', { tableId: activeTableIdRef.current });
      }
      clearLobbyReadyFallback();
      lobbyReadyTimerRef.current = setTimeout(() => {
        lobbyReadyTimerRef.current = null;
        // Never eject for slow state — keep requesting resync
        if (
          activeTableIdRef.current
          && !tableStateRef.current
          && !suppressTableViewRef.current
          && socket.connected
        ) {
          socket.emit('table:resync', { tableId: activeTableIdRef.current });
          socket.emit('lobby:heartbeat');
        }
      }, LOBBY_READY_FALLBACK_MS);
    });

    socket.on('connect_error', () => {
      showDisconnect('Could not connect to the lobby server.');
    });

    socket.on('lobby:error', (err) => {
      showDisconnect(err?.message || 'Lobby error');
    });

    // Admin changed lobby settings (allowed games) — update header tags immediately
    socket.on('lobby:config', (payload) => {
      if (Array.isArray(payload?.availableGames)) {
        setAvailableGames(payload.availableGames);
      }
      if (payload?.defaultGame) {
        setGameType(payload.defaultGame);
      }
    });

    socket.on('lobby:session-end', (payload) => {
      // Only full session death (replaced login / server restart). Do not treat as table stand.
      const reason = payload?.reason || '';
      if (reason === 'timeout' || reason === 'afk_timeout') {
        // Should be rare now; keep table UI and try to reconnect instead of ejecting
        setTableError(payload?.message || 'Connection issue — re-syncing…');
        setTimeout(() => setTableError(null), 6000);
        return;
      }
      exitTableView();
      showDisconnect(payload?.message || 'Session ended');
    });

    socket.on('lobby:ready', (payload) => {
      clearLobbyReadyFallback();
      if (Array.isArray(payload?.chatCommands)) {
        setChatCommands(payload.chatCommands);
      }
      if (Array.isArray(payload?.availableGames) && payload.availableGames.length) {
        setAvailableGames(payload.availableGames);
      }
      if (payload?.defaultGame) {
        setGameType(payload.defaultGame);
      }
      const resumeId = payload?.resumeTableId;
      if (resumeId) {
        // Server says we are still at a table (reconnect) — allow table UI again
        suppressTableViewRef.current = false;
        activeTableIdRef.current = resumeId;
        setActiveTableId(resumeId);
        persistTableId(resumeId);
        setChatTab('table');
        // Ask for fresh state after resume (avoids empty-state kick)
        socket.emit('table:resync', { tableId: resumeId });
        return;
      }
      // Keep sessionStorage table if server has not finished resume yet — resync, don't eject
      if (activeTableIdRef.current && !tableStateRef.current && !suppressTableViewRef.current) {
        socket.emit('table:resync', { tableId: activeTableIdRef.current });
      }
    });

    socket.on('lobby:history', (payload) => {
      setMessages(payload.messages || []);
    });

    socket.on('lobby:presence', (next) => {
      setPresence({
        count: next.count || 0,
        users: next.users || [],
      });
    });

    socket.on('lobby:chat', (msg) => {
      setMessages((prev) => [...prev, msg]);
      markChatUnread('lobby', msg);
    });

    socket.on('table:list', (payload) => {
      setTables(payload.tables || []);
    });

    socket.on('table:state', (payload) => {
      const table = payload.table;
      if (!table) return;
      // After Back to tables, late stand/leave broadcasts must not re-open TableView.
      if (suppressTableViewRef.current) return;
      const current = activeTableIdRef.current;
      if (!current || current !== table.id) return;
      setTableState((prev) => {
        let priv = privateViewerRef.current?.tableId === table.id
          ? privateViewerRef.current
          : null;
        if (!priv && prev?.id === table.id) {
          priv = privateFieldsFromTableState(prev);
        }
        return mergeTableState(table, priv, loggedInUserIdRef.current);
      });
    });

    socket.on('table:private', (payload) => {
      if (!payload?.tableId) return;
      if (suppressTableViewRef.current) return;
      const current = activeTableIdRef.current;
      if (!current || current !== payload.tableId) return;
      privateViewerRef.current = payload;
      setTableState((prev) => {
        if (!prev || prev.id !== payload.tableId) return prev;
        return mergeTableState(prev, payload, loggedInUserIdRef.current);
      });
    });

    socket.on('table:presence', (payload) => {
      if (suppressTableViewRef.current) return;
      if (activeTableIdRef.current === payload.tableId) {
        setTablePresence({
          count: payload.count || 0,
          users: payload.users || [],
        });
      }
    });

    socket.on('table:chat-history', (payload) => {
      if (suppressTableViewRef.current || !activeTableIdRef.current) return;
      const msgs = payload.messages || [];
      setTableMessages(msgs);
      // Don't toast history on join
      tableMsgLenRef.current = msgs.length;
    });

    socket.on('table:chat', (msg) => {
      setTableMessages((prev) => [...prev, msg]);
      markChatUnread('table', msg);
    });

    socket.on('table:leave', (payload) => {
      if (payload?.closeWindow) {
        window.close();
        // Still clear embedded state if close fails (popup blocked)
        exitTableView(payload?.message || null);
        return;
      }
      exitTableView(payload?.message || null);
    });

    socket.on('table:error', (err) => {
      const msg = err?.message || 'Table error';
      if (suppressTableViewRef.current) return;
      const atTable = !!activeTableIdRef.current;
      // Transient desync while still in table view — resync only, never stand/abandon
      if (atTable && TABLE_RESYNC_RE.test(msg)) {
        requestTableResync(msg);
        return;
      }
      // Hard gone only when table truly vanished — not during a slow first paint
      if (TABLE_GONE_RE.test(msg)) {
        exitTableView(msg);
        return;
      }
      // Illegal play/pass/bid (Spades + Hearts) → toast above south hand
      if (
        atTable
        && (err?.kind === 'game_action' || GAME_RULE_MSG_RE.test(msg))
      ) {
        showRuleToast(msg);
        return;
      }
      /* Keep other action errors visible longer — easy to miss after tabbing back. */
      setTableError(msg);
      setTimeout(() => setTableError(null), 8000);
    });

    socket.on('table:game-event', (payload) => {
      if (suppressTableViewRef.current) return;
      const ev = payload?.event;
      if (!ev || payload.tableId !== activeTableIdRef.current) return;
      if (ev.type === 'deal_card') {
        const visualSeat = visualSeatClass(ev.seat, viewerSeatRef.current);
        setDealFlash({ seat: ev.seat, visualSeat, step: ev.step });
        if (dealFlashTimerRef.current) clearTimeout(dealFlashTimerRef.current);
        dealFlashTimerRef.current = setTimeout(() => setDealFlash(null), 130);
      }
      if (typeof ev.type === 'string' && ev.type.startsWith('gin_')) {
        setGinGameEvent({ ...ev, _ts: Date.now() });
      }
    });

    heartbeatRef.current = setInterval(() => {
      if (socket.connected) {
        socket.emit('lobby:heartbeat');
      }
    }, HEARTBEAT_MS);
  }, [exitTableView, markChatUnread, persistTableId, requestTableResync, showDisconnect, showRuleToast, teardownSocket]);

  /** Tab return: browsers throttle timers — ping + resync so bidding does not soft-lock. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (!socket?.connected) return;
      socket.emit('lobby:heartbeat');
      if (suppressTableViewRef.current) return;
      const tableId = activeTableIdRef.current;
      if (tableId) {
        socket.emit('table:resync', { tableId });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(tableStorageKey(lobbyId));
    if (saved) {
      suppressTableViewRef.current = false;
      activeTableIdRef.current = saved;
      setActiveTableId(saved);
      setChatTab('table');
    }
  }, [lobbyId]);

  const attemptJoin = useCallback(async () => {
    if (!loggedInUser) {
      showDisconnect('You must be logged in to open a lobby.', true);
      return;
    }

    try {
      const res = await apiFetch(`/lobbies/${lobbyId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || (data && !data.ok && /logged in/i.test(data.message || ''))) {
        showDisconnect(data?.message || 'You must be logged in to open a lobby.', true);
        return;
      }
      if (!res.ok || !data.ok) {
        showDisconnect(data?.message || 'Cannot join lobby');
        return;
      }

      setTitle(data.lobby?.title || 'Lobby');
      setGameType(data.lobby?.defaultGame || data.lobby?.gameType || 'spades');
      if (Array.isArray(data.lobby?.availableGames)) {
        setAvailableGames(data.lobby.availableGames);
      } else if (Array.isArray(data.lobby?.allowedGames)) {
        setAvailableGames(data.lobby.allowedGames.map((id) => ({
          id,
          label: id.charAt(0).toUpperCase() + id.slice(1),
          playable: true,
        })));
      }
      setModeration(data.moderation || null);
      setCanManageLobbies(!!data.canManageLobbies);
      setChatCommands(mergeCommandCatalog(
        data.chatCommands,
        data.moderation || null,
        !!data.canManageLobbies,
      ));
      setChatMuted(!!(data.siteMuted || data.lobbyMuted));
      setDisconnectMsg(null);
      connectSocket(data);
    } catch {
      showDisconnect('Network error');
    }
  }, [connectSocket, lobbyId, loggedInUser, showDisconnect]);

  useEffect(() => {
    if (!sessionReady) return undefined;
    attemptJoin();
    return () => {
      teardownSocket();
      if (ruleToastTimerRef.current) {
        clearTimeout(ruleToastTimerRef.current);
        ruleToastTimerRef.current = null;
      }
    };
  }, [sessionReady, attemptJoin, teardownSocket]);

  // Slow first paint: keep resyncing — do NOT eject (false eject stood players / cleared bots)
  useEffect(() => {
    if (!activeTableId || tableState || !connected) return undefined;
    let attempts = 0;
    const tick = () => {
      if (!activeTableIdRef.current || tableStateRef.current || suppressTableViewRef.current) {
        return;
      }
      attempts += 1;
      requestTableResync(attempts >= 2 ? 'Reconnecting to table…' : null);
      // After many failures, offer cancel but still do not auto-leave the seat
      if (attempts >= 6) {
        setTableError('Still loading table — use Back to tables to leave, or wait…');
      }
    };
    const timer = setTimeout(tick, TABLE_LOAD_TIMEOUT_MS);
    const interval = setInterval(tick, TABLE_LOAD_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [activeTableId, tableState, connected, requestTableResync]);

  useEffect(() => {
    const ref = chatTab === 'table' ? tableMessagesEndRef : messagesEndRef;
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, tableMessages, chatTab]);

  const emitTable = (event, payload) => {
    if (!socketRef.current?.connected || !activeTableId) return;
    socketRef.current.emit(event, { tableId: activeTableId, ...payload });
  };

  const emitGameAction = (action, payload) => {
    emitTable('table:game-action', { action, payload });
  };

  const openTableView = (tableId) => {
    suppressTableViewRef.current = false;
    activeTableIdRef.current = tableId;
    setActiveTableId(tableId);
    setTableState(null);
    persistTableId(tableId);
    setChatTab('table');
  };

  const watchTable = (tableId) => {
    if (!socketRef.current?.connected) return;
    openTableView(tableId);
    socketRef.current.emit('table:watch', { tableId });
  };

  const sitAtTable = (tableId, seat) => {
    if (!socketRef.current?.connected) return;
    openTableView(tableId);
    socketRef.current.emit('table:sit', { tableId, seat });
  };

  const isLastHumanInPlayingTable = useCallback(() => {
    if (!tableState || tableState.status !== 'playing' || tableState.gameOver) return false;
    if (!tableState.viewerSeat) return false;
    const humans = SEAT_ORDER_MERGE.filter((s) => tableState.seats?.[s]?.type === 'player');
    return humans.length <= 1;
  }, [tableState]);

  const handleBackToTables = () => {
    const seated = !!(tableState?.viewerSeat
      || (tableState?.seats && seatForUserId(tableState.seats, currentUserId)));
    if (seated) {
      if (isLastHumanInPlayingTable()) {
        const ok = window.confirm(
          'You are the last player at this table. Leaving will end the game and reset the table. Are you sure?',
        );
        if (!ok) return;
      } else if (!window.confirm('Are you sure you want to leave the table? You will stand up from your seat.')) {
        return;
      }
    }
    // Always leave table shell immediately (seated → lobby grid in one click).
    // Server leave can arrive with a later table:state that used to re-open TableView.
    const tableId = activeTableIdRef.current || activeTableId;
    exitTableView();
    if (socketRef.current?.connected && tableId) {
      socketRef.current.emit('table:leave', { tableId });
    }
  };

  // Warn + try clean leave if last human closes/refreshes tab mid-game
  useEffect(() => {
    if (!inTableView) return undefined;

    const onBeforeUnload = (e) => {
      if (!isLastHumanInPlayingTable()) return;
      e.preventDefault();
      e.returnValue = '';
      try {
        if (socketRef.current?.connected && activeTableIdRef.current) {
          socketRef.current.emit('table:leave', { tableId: activeTableIdRef.current });
          socketRef.current.emit('lobby:goodbye');
        }
      } catch {
        /* unload */
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [inTableView, isLastHumanInPlayingTable]);

  const openModMenu = (user, rect) => {
    if (!canModerate) return;
    if (Number(user.userId) === Number(currentUserId)) return;
    setModTarget(user);
    setModAnchor(rect);
  };

  const handleTableKick = (targetUserId) => {
    emitTable('table:kick', { targetUserId });
    setModFlash('Removed from table');
    setTimeout(() => setModFlash(null), 3000);
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !socketRef.current?.connected || activeChatMuted) return;

    if (chatTab === 'table' && inTableView) {
      emitTable('table:chat', { text });
    } else {
      socketRef.current.emit('lobby:chat', { text });
    }
    setChatInput('');
    setChatCmdSuggestIdx(0);
  };

  const acceptChatCommandSuggestion = (cmd) => {
    if (!cmd?.name) return;
    setChatInput(applyCommandSuggestion(chatInput, cmd.name));
    setChatCmdSuggestIdx(0);
  };

  const handleChatInputKeyDown = (e) => {
    if (!chatCommandSuggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setChatCmdSuggestIdx((i) => (i + 1) % chatCommandSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setChatCmdSuggestIdx((i) => (
        (i - 1 + chatCommandSuggestions.length) % chatCommandSuggestions.length
      ));
    } else if (e.key === 'Tab') {
      e.preventDefault();
      acceptChatCommandSuggestion(
        chatCommandSuggestions[chatCmdSuggestIdx] || chatCommandSuggestions[0],
      );
    } else if (e.key === 'Escape') {
      setChatCmdSuggestIdx(0);
    }
  };

  const handleOpenTable = () => {
    if (!socketRef.current?.connected) return;
    const name = `Table ${tables.length + 1}`;
    socketRef.current.emit('table:open', { title: name });
  };

  const handleCloseTable = (tableId) => {
    if (!socketRef.current?.connected) return;
    if (!window.confirm('Remove this table?')) return;
    socketRef.current.emit('table:close', { tableId });
    if (activeTableId === tableId) {
      exitTableView('That table was removed.');
    }
  };

  return (
    <div className="lobby-room-page">
      {disconnectMsg && (
        <div className="lobby-overlay" role="alertdialog" aria-modal="true">
          <div className="lobby-overlay-panel">
            <h2>Disconnected</h2>
            <p>{disconnectMsg}</p>
            <Link to="/" className="lobby-overlay-btn">Back to lobby selection</Link>
          </div>
        </div>
      )}

      {modTarget && (
        <UserModMenu
          user={modTarget}
          lobbyId={lobbyId}
          moderation={moderation}
          anchorRect={modAnchor}
          tableContext={inTableView ? { tableId: activeTableId } : null}
          onTableKick={handleTableKick}
          onClose={() => {
            setModTarget(null);
            setModAnchor(null);
          }}
          onActionComplete={(msg) => {
            setModFlash(msg);
            setTimeout(() => setModFlash(null), 3000);
          }}
        />
      )}

      <div
        className={[
          'lobby-shell',
          disconnectMsg ? 'lobby-shell--inactive' : '',
          inTableView ? 'lobby-shell--at-table' : '',
        ].filter(Boolean).join(' ')}
      >
        <header className="lobby-top">
          <h1 className="lobby-top-title">
            {title}
            <span className="lobby-game-tags" aria-label="Games in this lobby">
              {(availableGames.length
                ? availableGames
                : [{ id: gameType || 'spades', label: gameType === 'hearts' ? 'Hearts' : 'Spades' }]
              ).map((g) => (
                <span
                  key={g.id}
                  className={[
                    'lobby-game-tag',
                    g.playable === false ? 'lobby-game-tag--soon' : '',
                  ].filter(Boolean).join(' ')}
                  title={g.description || g.label}
                >
                  {g.label || g.id}
                </span>
              ))}
            </span>
          </h1>
          <div className="lobby-count">
            Online: <span>{presence.count}</span>
            {connected && <span className="lobby-live-dot" title="Connected" aria-hidden="true" />}
            {modFlash && <span className="lobby-mod-flash">{modFlash}</span>}
            {tableError && <span className="lobby-table-error">{tableError}</span>}
          </div>
        </header>

        <section className="lobby-main">
          {activeTableId && !tableState ? (
            <div className="table-grid-empty table-grid-empty--loading">
              <p>Loading table…</p>
              <p className="table-grid-loading-hint">If this hangs, wait for resync or leave the table below.</p>
              <button type="button" className="table-grid-back-btn" onClick={handleBackToTables}>
                Back to tables
              </button>
            </div>
          ) : inTableView ? (
            <TableView
              table={tableState}
              loggedInUserId={currentUserId}
              dealFlash={dealFlash}
              ginGameEvent={ginGameEvent}
              onBack={handleBackToTables}
              onSit={(seat) => emitTable('table:sit', { seat })}
              onStand={() => emitTable('table:stand')}
              onHostAction={(action, payload) => emitTable('table:host-action', { action, payload })}
              onPeekHand={(seat) => emitTable('table:peek-hand', { seat })}
              onGameAction={emitGameAction}
              tablePresence={tablePresence}
              canModerate={canModerate}
              onUserModClick={openModMenu}
              chatBubbles={chatBubbles}
              ruleToast={ruleToast}
            />
          ) : (
            <TableGrid
              tables={tables}
              canManageLobbies={canManageLobbies}
              onOpenTable={handleOpenTable}
              onCloseTable={handleCloseTable}
              onWatchTable={watchTable}
              onSitAtTable={sitAtTable}
            />
          )}
        </section>

        {/* Lobby browser only — at-table presence/scores live on the felt */}
        {!inTableView && (
          <aside className="lobby-sidebar">
            <h2>{sidebarTitle} ({sidebarCount})</h2>
            <ul className="lobby-user-list">
              {sidebarUsers.map((u) => (
                <li key={String(u.userId ?? u.username)}>
                  {canModerate && Number(u.userId) !== Number(currentUserId) ? (
                    <button
                      type="button"
                      className="lobby-user-btn"
                      onClick={(e) => openModMenu(u, e.currentTarget.getBoundingClientRect())}
                    >
                      <LobbyUserAvatar
                        username={u.username}
                        avatarUrl={u.avatarUrl}
                        isHost={!!u.isHost}
                      />
                      <ChatBadges roleBadge={u.roleBadge} clubBadge={u.clubBadge} />
                      <span className="lobby-user-name">{u.username}</span>
                    </button>
                  ) : (
                    <span className="lobby-user-line">
                      <LobbyUserAvatar
                        username={u.username}
                        avatarUrl={u.avatarUrl}
                        isHost={!!u.isHost}
                      />
                      <ChatBadges roleBadge={u.roleBadge} clubBadge={u.clubBadge} />
                      <span className="lobby-user-name">{u.username}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </aside>
        )}

        <section className={`lobby-chat ${inTableView ? 'lobby-chat--at-table' : ''}`}>
          {inTableView && (
            <div className="lobby-chat-tabs">
              <button
                type="button"
                className={[
                  'lobby-chat-tab',
                  chatTab === 'lobby' ? 'lobby-chat-tab--active' : '',
                  chatTab !== 'lobby' && chatUnread.lobby
                    ? (chatUnread.lobbyImportant ? 'lobby-chat-tab--unread-important' : 'lobby-chat-tab--unread')
                    : '',
                ].filter(Boolean).join(' ')}
                onClick={() => selectChatTab('lobby')}
              >
                Lobby
              </button>
              <button
                type="button"
                className={[
                  'lobby-chat-tab',
                  chatTab === 'table' ? 'lobby-chat-tab--active' : '',
                  chatTab !== 'table' && chatUnread.table
                    ? (chatUnread.tableImportant ? 'lobby-chat-tab--unread-important' : 'lobby-chat-tab--unread')
                    : '',
                ].filter(Boolean).join(' ')}
                onClick={() => selectChatTab('table')}
              >
                Table
              </button>
            </div>
          )}
          <div className="lobby-messages">
            {activeMessages.map((msg, idx) => (
              <div
                key={`${msg.ts || idx}-${msg.username || 'sys'}-${msg.text}`}
                className={[
                  'lobby-msg',
                  msg.type || 'user',
                  msg.highlight ? 'lobby-msg--highlight' : '',
                ].filter(Boolean).join(' ')}
              >
                {msg.type === 'system' ? (
                  msg.text
                ) : (
                  <>
                    <ChatUsername
                      msg={msg}
                      canModerate={canModerate}
                      onClick={openModMenu}
                    />
                    <span className="lobby-msg-colon">: </span>
                    {msg.text || ''}
                  </>
                )}
              </div>
            ))}
            <div ref={chatTab === 'table' ? tableMessagesEndRef : messagesEndRef} />
          </div>
          <form className="lobby-chat-form" onSubmit={handleChatSubmit}>
            <div className="lobby-chat-input-wrap">
              {chatCommandSuggestions.length > 0 && (
                <ul className="lobby-chat-cmd-suggest" role="listbox" aria-label="Chat commands">
                  {chatCommandSuggestions.map((cmd, idx) => (
                    <li key={cmd.name}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={idx === chatCmdSuggestIdx}
                        className={[
                          'lobby-chat-cmd-suggest-item',
                          idx === chatCmdSuggestIdx ? 'lobby-chat-cmd-suggest-item--active' : '',
                        ].filter(Boolean).join(' ')}
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          acceptChatCommandSuggestion(cmd);
                        }}
                      >
                        <span className="lobby-chat-cmd-suggest-name">!{cmd.name}</span>
                        <span className="lobby-chat-cmd-suggest-desc">{cmd.description}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                type="text"
                maxLength={500}
                placeholder={
                  activeChatMuted
                    ? 'You are muted'
                    : chatTab === 'table' && inTableView
                      ? 'Table chat…'
                      : chatCommands.length
                        ? 'Lobby chat… (! for commands)'
                        : 'Lobby chat…'
                }
                autoComplete="off"
                value={chatInput}
                onChange={(e) => {
                  setChatInput(e.target.value);
                  setChatCmdSuggestIdx(0);
                }}
                onKeyDown={handleChatInputKeyDown}
                disabled={!connected || activeChatMuted}
              />
            </div>
            <button type="submit" className="lobby-chat-send" disabled={!connected || activeChatMuted}>
              Send
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

export default LobbyRoom;